import { addClass, removeClass, toggleClass } from '@studiometa/js-toolkit/utils';
import ListingBase from './ListingBase.js';
import ListingLetters from './ListingLetters.js';
import ListingTags from './ListingTags.js';
import Shuffle from './Shuffle.js';

Shuffle.ShuffleItem.Css = {
  ...Shuffle.ShuffleItem.Css,
  INITIAL: {
    ...Shuffle.ShuffleItem.Css.INITIAL,
    transformOrigin: 'top left',
  },
};

/**
 * Listing Static component
 */
export default class ListingStatic extends ListingBase {
  static config = {
    name: 'ListingStatic',
    refs: ['sections[]', 'empty'],
    options: {
      parameters: {
        type: Array,
      },
    },
    components: {
      ...ListingBase.config.components,
      ListingLetters,
      ListingTags,
    },
  };

  shuffleInstances = [];

  /**
   * Mounted
   */
  mounted() {
    this.$refs.sections.forEach((section) => {
      const matchingAllItems = [...section.querySelectorAll('.js-section-match-all-items')];
      const matchingSingleItem = [...section.querySelectorAll('.js-section-match-single-item')];

      // Assign matchingSingleItem attributes to matchingAllItems
      matchingAllItems.forEach((el) => {
        this.$options.parameters.forEach((parameter) => {
          const values = [...new Set(matchingSingleItem.map((item) => item.dataset[parameter]))];
          const filteredValues = values.filter((item) => item !== undefined);
          el.setAttribute(`data-${parameter}`, filteredValues.join(','));
        });
      });

      const shuffleInstance = new Shuffle(section, {
        itemSelector: '.js-section-match-single-item, .js-section-match-all-items',
        delimiter: ',',
        sizer: section.querySelector('.sizer'),
        speed: 400,
        staggerAmount: 0,
      });

      this.shuffleInstances.push(shuffleInstance);
    });

    this.searchField = document.querySelector('#search-input');

    if (this.searchField) {
      this.searchField.addEventListener('input', this.onSearchChange.bind(this));
    }

    this.setup();
    this.setupListeners();
  }

  /**
   * Get fields data
   * @returns {object} data
   */
  getData() {
    const data = Object.fromEntries(
      this.fields
        .filter((field) => field.value && field.value !== '-1')
        .map((field) => [field.name, field.value]),
    );

    return data;
  }

  /**
   * Update the listing on fields change
   */
  onFieldsChange() {
    this.$children.ListingTags.forEach((listingTags) => {
      listingTags.render(this.fields);
    });

    this.$refs.sections.forEach((element) => {
      removeClass(element, 'hidden-important');
    });

    const data = this.getData();

    this.shuffleInstances.forEach((instance) => {
      instance.filter((el) => {
        return this.$options.parameters.every((parameter) => {
          // Replace - with _ to match drupal taxonomies. Ex: entry_level ≠ entry-level
          const p = parameter.replace('-', '_');

          // Convert to camelCase since data-entry-level is dataset.entryLevel in js.
          return data[p]
            ? el.dataset[this.toCamel(parameter)] &&
                el.dataset[this.toCamel(parameter)].includes(data[p])
            : true;
        });
      });
    });

    let hasNoResults = false;
    let hasSectionResults = false;
    this.$refs.sections.forEach((element) => {
      hasSectionResults = !!element.querySelectorAll(
        '.shuffle-item--visible:not(.js-section-match-all-items)',
      ).length;
      hasNoResults = hasSectionResults || hasNoResults;

      if (!hasSectionResults) {
        addClass(element, 'hidden-important');
      }
    });

    toggleClass(this.$refs.empty, 'hidden', hasNoResults);
  }

  /**
   * Convert a string to camelCase
   * @param {string} s the string to convert
   * @returns {string}
   */
  toCamel(s) {
    return s.replaceAll(/([-_][a-z])/gi, ($1) => {
      return $1.toUpperCase().replace('-', '').replace('_', '');
    });
  }

  /**
   * Filter elements by search
   * @param {Event} e
   */
  onSearchChange(e) {
    const data = this.getData();

    const searchText = e.target.value.toLowerCase().trim();

    this.shuffleInstances.forEach((instance) => {
      instance.filter((el) => {
        const matchData = this.$options.parameters.every((parameter) =>
          data[parameter]
            ? el.dataset[parameter] && el.dataset[parameter].includes(data[parameter])
            : true,
        );

        const matchSearchText = el.dataset.search
          ? el.dataset.search.toLowerCase().trim().includes(searchText)
          : true;

        return matchData && matchSearchText;
      });
    });
  }

  /**
   * Reset filters and trigger change
   */
  onListingPanelReset() {
    super.onListingPanelReset();
    this.onFieldsChange();
  }

  /**
   * Trigger filter on letters change
   */
  onListingLettersChange() {
    this.onFieldsChange();
  }

  /**
   * Trigger filter on tags change
   */
  onListingTagsChange() {
    this.onFieldsChange();
  }
}
